import React, { useState, useEffect, useMemo, useRef, useContext } from "react";

import { useHistory, useParams } from "react-router-dom";
import { parseISO, format, isSameDay } from "date-fns";
import clsx from "clsx";

import { makeStyles } from "@material-ui/core/styles";
import { green, grey, red, blue } from "@material-ui/core/colors";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import Typography from "@material-ui/core/Typography";
import Avatar from "@material-ui/core/Avatar";
import Divider from "@material-ui/core/Divider";
import Badge from "@material-ui/core/Badge";
import Box from "@material-ui/core/Box";
import Checkbox from "@material-ui/core/Checkbox";

import { i18n } from "../../translate/i18n";
import { translateMessageMarker } from "../../utils/messagePreview";
import { translateSystemMessage } from "../../helpers/systemMessageText";

import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";

import MarkdownWrapper from "../MarkdownWrapper";
import { Tooltip } from "@material-ui/core";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import { useMutedContacts } from "../../context/MutedContacts/MutedContactsContext";
import { useContactTyping } from "../../hooks/useContactWhatsappStatus";
import { PRESENCE_HIGHLIGHT_COLOR } from "../../helpers/contactWhatsappStatus";
import toastError from "../../errors/toastError";
import { v4 as uuidv4 } from "uuid";

import RoomIcon from '@material-ui/icons/Room';
import EmailIcon from "@material-ui/icons/Email";
import ChannelIcon from "../../helpers/channelIcon";
import HeadsetMicIcon from "@material-ui/icons/HeadsetMic";
import AccountTreeOutlinedIcon from "@material-ui/icons/AccountTreeOutlined";
import AndroidIcon from "@material-ui/icons/Android";
import RobotIcon from "../RobotIcon";
import VisibilityIcon from "@material-ui/icons/Visibility";
import TicketMessagesDialog from "../TicketMessagesDialog";
import DoneIcon from '@material-ui/icons/Done';
import DoneAllIcon from '@material-ui/icons/DoneAll';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import ClearOutlinedIcon from '@material-ui/icons/ClearOutlined';
import MutedIcon from '@material-ui/icons/NotificationsOff';
import contrastColor from "../../helpers/contrastColor";
import { isBadgesTicketCardLayout } from "../../helpers/ticketCardLayout";
import useBadgeRowWidths, { BADGE_TEXT_ATTR } from "../../hooks/useBadgeRowWidths";
import ContactTag from "../ContactTag";
import ContactAvatar from "../ContactAvatar";
import useCloseTicketGuard from "../../hooks/useCloseTicketGuard";
import InactiveConnectionBadge from "../InactiveConnectionBadge";
import { isInactiveTicket } from "../../helpers/canAccessTicket";

// Cache (compartilhado entre itens da lista) de promptId → nome do agente de IA,
// para quando o payload do ticket chega sem a associação prompt
const promptNameCache = {};

const getLastMessagePreview = (message) => {
  if (!message) return "";

  // Marcadores de mídia gravados em PT pelo backend (Áudio, Imagem...) e a
  // localização (thumb base64) viram texto no idioma da interface.
  const normalized = translateSystemMessage(translateMessageMarker(message)).replace(/\s+/g, " ").trim();

  return normalized.length > 120 ? `${normalized.substring(0, 120)}...` : normalized;
};

// Teto de largura dos badges de fila/conexão e do chip do atendente no card do
// ticket. Nome que não couber é cortado com reticências (o nome inteiro continua
// no tooltip). Mexer aqui é o único ponto para deixar os badges mais largos.
const BADGE_MAX_WIDTH = 150;

// Piso do truncamento: por mais apertada que fique a linha, nenhum dos três
// itens pode encolher a ponto de mostrar menos texto que isto. Nome mais curto
// que o piso nunca é truncado. Quem distribui a largura é useBadgeRowWidths.
const BADGE_MIN_CHARS = 10; // fila e conexão
const ATTENDANT_MIN_CHARS = 12; // atendente: piso maior porque tem preferência
const BADGE_FONT_SIZE_REM = 0.7; // igual ao fontSize de infoBadge/attendantChip
const INFO_BADGE_LETTER_SPACING = 0.3; // px, aplicado em infoBadge
const BADGE_ROW_GAP = 6; // igual ao gap de badgeRows

// Ordem em que os itens cedem espaço quando a linha aperta: fila e conexão
// encolhem juntas até o piso delas e só então o atendente começa a ceder.
const SHRINK_FIRST = 0;
const SHRINK_LAST = 1;

// Largura que cada item gasta além do texto (ícone + gap + padding), em px.
const INFO_BADGE_CHROME = 25; // padding 4+4, ícone 13, gap 4
const ATTENDANT_CHROME = 18; // ícone 15, gap 3
// Folga extra do chip do atendente quando o badge vermelho de não lidas ocupa a
// direita do card (ver attendantChipUnread).
const ATTENDANT_UNREAD_OFFSET = 16;

const useStyles = makeStyles((theme) => ({
  "@keyframes ticketAlarmBlink": {
    "0%, 45%": {
      background: red[700],
      borderLeftColor: "#b71c1c",
    },
    "55%, 100%": {
      background: theme.palette.background.paper,
      borderLeftColor: "transparent",
    },
  },

  "@keyframes ticketAlarmPrimaryTextBlink": {
    "0%, 45%": {
      color: "#fff",
    },
    "55%, 100%": {
      color: theme.palette.text.primary,
    },
  },

  "@keyframes ticketAlarmSecondaryTextBlink": {
    "0%, 45%": {
      color: "rgba(255,255,255,0.92)",
    },
    "55%, 100%": {
      color: theme.palette.text.secondary,
    },
  },

  "@keyframes ticketAlarmReadIconBlink": {
    "0%, 45%": {
      color: "#fff",
    },
    "55%, 100%": {
      color: blue[500],
    },
  },

  ticket: {
    position: "relative",
    borderLeft: "3px solid transparent",
    // sobrescreve o padding vertical do ListItem dense (4px) só nos cards de ticket
    paddingTop: 4,
    paddingBottom: 4,
    // e a margem vertical (6px) do ListItemText quando ele fica multiline
    "& .MuiListItemText-multiline": {
      marginTop: 0,
      marginBottom: 0,
    },
    // e a entrelinha padrão do body2 (1.43), que deixa nome e prévia altos demais
    "& .MuiTypography-body2": {
      lineHeight: 1.33,
    },
    // No tema escuro o cinza fixo sumia sobre o fundo do card — nome do contato
    // fica branco quando palette.type === "dark".
    "& .MuiTypography-colorTextPrimary": {
      color:
        theme.palette.type === "dark" ? "#FFFFFF" : "rgba(61, 61, 61, 0.87)",
    },
  },

  ticketAlarm: {
    background: red[700],
    borderLeft: "3px solid #b71c1c",
    animation: "$ticketAlarmBlink 1.4s ease-in-out infinite",
    transition: "background 0.35s ease, border-left-color 0.35s ease",
    "& .MuiTypography-colorTextPrimary": {
      animation: "$ticketAlarmPrimaryTextBlink 1.4s ease-in-out infinite",
    },
    "& .MuiTypography-colorTextSecondary": {
      animation: "$ticketAlarmSecondaryTextBlink 1.4s ease-in-out infinite",
    },
    "& $lastMessageTime": {
      animation: "$ticketAlarmSecondaryTextBlink 1.4s ease-in-out infinite",
    },
    "& $messageCheckIcon": {
      animation: "$ticketAlarmSecondaryTextBlink 1.4s ease-in-out infinite",
    },
    "& $messageCheckIconDouble": {
      animation: "$ticketAlarmSecondaryTextBlink 1.4s ease-in-out infinite",
    },
    "& $messageCheckIconRead": {
      animation: "$ticketAlarmReadIconBlink 1.4s ease-in-out infinite",
    },
    "& .MuiListItemSecondaryAction-root .MuiTypography-root": {
      animation: "$ticketAlarmSecondaryTextBlink 1.4s ease-in-out infinite",
    },
    "& .MuiListItemSecondaryAction-root $lastMessageTime": {
      animation: "$ticketAlarmSecondaryTextBlink 1.4s ease-in-out infinite",
    },
  },

  pendingTicket: {
    cursor: "pointer",
  },

  selectedTicket: {
    backgroundColor: theme.palette.action.selected + " !important",
  },

  checkboxContainer: {
    position: "absolute",
    left: 12,
    top: "50%",
    transform: "translateY(-50%)",
    zIndex: 2,
  },

  ticketWithCheckbox: {
    paddingLeft: 48,
  },

  queueTag: {
    background: "#FCFCFC",
    color: "#000",
    marginRight: 1,
    padding: 1,
    fontWeight: 'bold',
    paddingLeft: 5,
    paddingRight: 5,
    borderRadius: 3,
    fontSize: "0.8em",
    whiteSpace: "nowrap"
  },
  noTicketsDiv: {
    display: "flex",
    height: "100px",
    margin: 40,
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  newMessagesCount: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: 20,
  },
  noTicketsText: {
    textAlign: "center",
    color: "rgb(104, 121, 146)",
    fontSize: "14px",
    lineHeight: "1.4",
  },
  connectionTag: {
    background: "green",
    color: "#FFF",
    marginRight: 4,
    marginBottom: 2,
    padding: 1,
    fontWeight: 'bold',
    paddingLeft: 5,
    paddingRight: 5,
    borderRadius: 3,
    fontSize: "0.75em",
    whiteSpace: "nowrap",
    display: "inline-block"
  },
  bottomRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    marginTop: 4,
    marginRight: -32,
  },
  queueLine: {
    fontSize: "0.7rem",
    fontWeight: "bold",
    lineHeight: 1,
    textTransform: "uppercase",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    minWidth: 0,
  },
  bottomRightInfo: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    flexShrink: 0,
  },
  attendantChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "3px",
    fontSize: `${BADGE_FONT_SIZE_REM}rem`,
    fontWeight: "bold",
    lineHeight: 1,
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    // acompanha a largura do wrapper (badgeAttendant), que é quem carrega o
    // piso de ATTENDANT_MIN_CHARS e o teto de BADGE_MAX_WIDTH
    maxWidth: "100%",
    minWidth: 0,
    // sem isso o nome do atendente simplesmente transborda o maxWidth acima
    overflow: "hidden",
  },
  attendantChipUnread: {
    // afasta o chip do badge vermelho de não lidas, que invade a direita do card
    marginRight: ATTENDANT_UNREAD_OFFSET,
  },
  attendantName: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    // item de flex nasce com min-width:auto e se recusa a encolher abaixo do
    // conteudo; sem isso o ellipsis acima nunca chega a ser aplicado
    minWidth: 0,
  },
  attendantIcon: {
    fontSize: 15,
    flexShrink: 0,
  },
  connectionChip: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#FFF",
    padding: 3,
    borderRadius: 3,
  },

  // ----- Layout alternativo (REACT_APP_TICKET_CARD_LAYOUT=badges) -----
  ticketBadgesLayout: {
    minHeight: 40,
  },
  contactNameBadges: {
    fontSize: 16,
    fontWeight: 700,
  },
  badgeRows: {
    display: "flex",
    // fila, conexão e atendente ficam sempre na MESMA linha; quando não cabem,
    // cada um encolhe e corta com reticências em vez de quebrar linha
    flexWrap: "nowrap",
    alignItems: "center",
    gap: 6,
    marginTop: 0,
    marginRight: -32,
    minWidth: 0,
    // com o piso de BADGE_MIN_CHARS os três itens podem, num card muito estreito,
    // somar mais que a linha; aqui o excesso é cortado dentro do card em vez de
    // vazar por cima da lista
    overflow: "hidden",
  },
  badgeAttendant: {
    display: "inline-flex",
    alignItems: "center",
    marginLeft: "auto",
    maxWidth: BADGE_MAX_WIDTH,
    // encolhe ~3x mais devagar que fila/conexão: quando a linha aperta, são os
    // badges que cedem primeiro e o nome do atendente é o último a ser cortado.
    // O piso em px vai inline (badgeMinWidth), aqui não pode haver minWidth: 0.
    flexShrink: 0.35,
    overflow: "hidden",
  },
  infoBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "0px 4px",
    borderRadius: 4,
    fontSize: `${BADGE_FONT_SIZE_REM}rem`,
    fontWeight: "bold",
    lineHeight: 1.5,
    letterSpacing: `${INFO_BADGE_LETTER_SPACING}px`,
    textTransform: "uppercase",
    whiteSpace: "nowrap",
    overflow: "hidden",
    minWidth: 0,
    // badgeRows avança 32px para fora do card (marginRight negativo) para o chip
    // do atendente encostar na direita; descontamos isso aqui para o badge nunca
    // passar da borda visível do card.
    maxWidth: "calc(100% - 32px)",
    "& svg": {
      fontSize: 13,
      flexShrink: 0,
    },
  },
  // O ellipsis precisa estar num filho block/inline, não no proprio inline-flex:
  // texto solto dentro de um container flex vira item anonimo e ignora
  // text-overflow, e era por isso que nome de conexao longo vazava do card.
  infoBadgeLabel: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
  },
  noTicketsTitle: {
    textAlign: "center",
    fontSize: "16px",
    fontWeight: "600",
    margin: "0px",
  },

  contactNameWrapper: {
    display: "flex",
    justifyContent: "space-between",
    marginLeft: "5px",
  },

  mutedBadgeRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
  },

  mutedIcon: {
    fontSize: "1.05rem",
    color: theme.palette.text.secondary,
    flexShrink: 0,
  },

  mutedIconWithBadge: {
    marginRight: 18,
  },

  lastMessageTime: {
    justifySelf: "flex-end",
    textAlign: "right",
    position: "relative",
    top: 0,
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
  },

  lastMessageTimeAlarm: {
    animation: "$ticketAlarmSecondaryTextBlink 1.4s ease-in-out infinite",
  },

  messageCheckIcon: {
    fontSize: "14px",
    marginRight: "4px",
    verticalAlign: "middle",
    color: grey[600],
  },

  messageCheckIconRead: {
    fontSize: "14px",
    marginRight: "4px",
    verticalAlign: "middle",
    color: blue[500],
  },

  messageCheckIconDouble: {
    fontSize: "14px",
    marginLeft: "-8px",
    marginRight: "4px",
    verticalAlign: "middle",
    color: grey[600],
  },

  // Mesmo vermelho do ackErrorIcon do MessagesList, para a falha de envio ter a
  // mesma leitura na lista de tickets e dentro da conversa.
  messageCheckIconError: {
    fontSize: "14px",
    marginRight: "4px",
    verticalAlign: "middle",
    color: "#e53935",
  },

  closedBadge: {
    alignSelf: "center",
    justifySelf: "flex-end",
    marginRight: 32,
    marginLeft: "auto",
  },

  contactLastMessage: {
    paddingRight: "0%",
    fontSize: 16,
  },

  contactTyping: {
    color: PRESENCE_HIGHLIGHT_COLOR,
  },

  secondaryWrapper: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    marginLeft: "5px",
  },


  badgeStyle: {
    color: "white",
    backgroundColor: "red",
    "&.MuiBadge-anchorOriginTopRightRectangle": {
      top: -10,
      right: 0,
      transform: "scale(1) translate(50%, -50%)",
      transformOrigin: "100% 0%",
    },
  },

  unreadDotBadge: {
    "& .MuiBadge-badge": {
      height: 20,
      minWidth: 20,
      borderRadius: 19,
    },
  },

  acceptButton: {
    position: "absolute",
    right: "108px",
  },


  acceptButton: {
    position: "absolute",
    left: "50%",
  },


  ticketQueueColor: {
    flex: "none",
    width: "8px",
    height: "100%",
    position: "absolute",
    top: "0%",
    left: "0%",
  },

  ticketInfo: {
    position: "relative",
    top: -13
  },
  secondaryContentSecond: {
    display: 'flex',
    // marginTop: 5,
    //marginLeft: "5px",
    alignItems: "flex-start",
    flexWrap: "wrap",
    flexDirection: "row",
    alignContent: "flex-start",
  },
  ticketInfo1: {
    position: "relative",
    top: 13,
    right: 0
  },
  Radiusdot: {
    "& .MuiBadge-badge": {
      borderRadius: 2,
      position: "inherit",
      height: 16,
      margin: 2,
      padding: 3
    },
    "& .MuiBadge-anchorOriginTopRightRectangle": {
      transform: "scale(1) translate(0%, -40%)",
    },

  }
}));
  {/*PLW DESIGN INSERIDO O dentro do const handleChangeTab*/}
  const TicketListItemCustom = ({ 
    ticket, 
    selectionMode = false, 
    isSelected = false, 
    onSelect, 
    onContextMenu,
    selectionStatus
  }) => {
  const classes = useStyles();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [ticketUser, setTicketUser] = useState(null);
  const [aiAgentName, setAiAgentName] = useState(ticket.prompt?.name || null);
  const [ticketQueueName, setTicketQueueName] = useState(null);
  const [ticketQueueColor, setTicketQueueColor] = useState(null);
  const [tag, setTag] = useState([]);
  const [whatsAppName, setWhatsAppName] = useState(null);

  const [openTicketMessageDialog, setOpenTicketMessageDialog] = useState(false);
  const { ticketId } = useParams();
  const isMounted = useRef(true);
  const { setCurrentTicket } = useContext(TicketsContext);
  const { user } = useContext(AuthContext);
  // Aviso/bloqueio de finalização sem interação com o contato (config da empresa)
  const { closeTicket, dialog: closeGuardDialog } = useCloseTicketGuard();

  const { profile } = user;

  // Layout do card definido em .env (REACT_APP_TICKET_CARD_LAYOUT)
  const badgesLayout = isBadgesTicketCardLayout;

  // Contato silenciado por ESTE usuário (não toca som nem notifica no desktop)
  const { isContactMuted } = useMutedContacts();
  const isTicketMuted = isContactMuted(ticket?.contact?.id || ticket?.contactId);

  // "digitando..." no lugar da última mensagem, como na lista do WhatsApp.
  const contactTyping = useContactTyping(ticket?.contact?.id || ticket?.contactId);

  // Verifica se este ticket pode ser selecionado (mesmo status)
  const canSelect = !selectionStatus || ticket.status === selectionStatus;

  // Nome do agente de IA: usa o objeto prompt quando o payload traz; senão
  // busca uma vez pelo promptId e guarda em cache (payloads de socket podem
  // vir sem a associação prompt)
  useEffect(() => {
    if (!ticket.promptId) {
      setAiAgentName(null);
      return;
    }
    if (ticket.prompt?.name) {
      promptNameCache[ticket.promptId] = ticket.prompt.name;
      setAiAgentName(ticket.prompt.name);
      return;
    }
    if (promptNameCache[ticket.promptId]) {
      setAiAgentName(promptNameCache[ticket.promptId]);
      return;
    }
    let cancelled = false;
    api.get(`/prompt/${ticket.promptId}`)
      .then(({ data }) => {
        if (!cancelled && data?.name) {
          promptNameCache[ticket.promptId] = data.name;
          setAiAgentName(data.name);
        }
      })
      .catch(() => { /* mantém fallback */ });
    return () => { cancelled = true; };
  }, [ticket.promptId, ticket.prompt?.name]);

  useEffect(() => {
    const userName = ticket.user?.name?.toUpperCase();
    const agentName = aiAgentName?.toUpperCase();

    if (userName && agentName) {
      setTicketUser(`${userName} (${agentName})`);
    } else if (agentName) {
      setTicketUser(agentName);
    } else if (ticket.userId && userName) {
      setTicketUser(userName);
    } else {
      setTicketUser(null);
    }
    setTicketQueueName(ticket.queue?.name?.toUpperCase());
    setTicketQueueColor(ticket.queue?.color);

    if (ticket.whatsappId && ticket.whatsapp) {
      setWhatsAppName(ticket.whatsapp?.name?.toUpperCase() || null);
    } else {
      setWhatsAppName(null);
    }

    setTag(ticket?.tags);

    return () => {
      isMounted.current = false;
    };
  }, [ticket, ticket.userId, ticket.user?.id, ticket.user?.name, aiAgentName, ticket.queue?.id, ticket.queue?.name, ticket.queue?.color, ticket.whatsappId, ticket.whatsapp?.name, ticket.tags]);

  {/*CÓDIGO NOVO SAUDAÇÃO*/}
  const handleCloseTicket = async (id) => {
    setTag(ticket?.tags);
    setLoading(true);

    // closeTicket cuida do aviso/bloqueio de finalização sem interação com o contato
    const closed = await closeTicket(id, {
      userId: user?.id,
      queueId: ticket?.queue?.id,
      useIntegration: false,
      promptId: null,
      integrationId: null
    });

    if (isMounted.current) {
      setLoading(false);
    }

    if (!closed) return;

    history.push(`/tickets/`);
  };

  const handleReopenTicket = async (id) => {
    // Ticket de conexão excluída é só leitura: não reabre (o backend também recusa).
    if (isInactiveTicket(ticket)) return;
    setLoading(true);
    try {
      await api.put(`/tickets/${id}`, {
        status: "open",
        userId: user?.id,
        queueId: ticket?.queue?.id
      });
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
    if (isMounted.current) {
      setLoading(false);
    }
    history.push(`/tickets/${ticket.uuid}`);
  };

    const handleAcepptTicket = async (id) => {
        setLoading(true);
        try {
            // Buscar dados do ticket para verificar a fila
            const { data: ticketData } = await api.get(`/tickets/${id}`);
            
            // Verificar se o ticket tem fila e se o usuário tem acesso a ela
            if (ticketData.queueId) {
                const userQueueIds = user.queues.map((q) => q.id);
                if (!userQueueIds.includes(ticketData.queueId)) {
                    setLoading(false);
                    toastError(i18n.t("tickets.toasts.noQueueAccess"));
                    return;
                }
            }
            
            await api.put(`/tickets/${id}`, {
                status: "open",
                userId: user?.id,
            });
            
            let settingIndex;

            try {
                const { data } = await api.get("/settings/");
                
                settingIndex = data.filter((s) => s.key === "sendGreetingAccepted");
                
            } catch (err) {
                toastError(err);
                   
            }
            
            if (settingIndex && settingIndex[0] && settingIndex[0].value === "enabled" && !ticket.isGroup) {
                handleSendMessage(ticket.id);
                
            }

        } catch (err) {
            setLoading(false);
            
            toastError(err);
        }
        if (isMounted.current) {
            setLoading(false);
        }

        // handleChangeTab(null, "tickets");
        // handleChangeTab(null, "open");
        history.push(`/tickets/${ticket.uuid}`);
    };
	
	    const handleSendMessage = async (id) => {
        
        const msg = `{{ms}} *{{name}}*, meu nome é *${user?.name}* e agora vou prosseguir com seu atendimento!`;
        const message = {
            read: 1,
            fromMe: true,
            mediaUrl: "",
            body: `*Mensagem Automática:*\n${msg.trim()}`,
        };
        try {
            await api.post(`/messages/${id}`, message);
        } catch (err) {
            toastError(err);
            
        }
    };
	{/*CÓDIGO NOVO SAUDAÇÃO*/}

  const handleSelectTicket = async (ticket) => {
    const code = uuidv4();
    const { id, uuid } = ticket;
    setCurrentTicket({ id, uuid, code });

    // PERF: fire-and-forget. Não bloqueia a navegação para abrir o ticket.
    // O ticket abre instantaneamente; a marcação como lido roda em background
    // e o backend emite WebSocket para atualizar o badge depois.
    api.post(`/messages/${id}/read`).catch((err) => {
      console.error("[TicketListItemCustom] Erro ao marcar ticket como lido", {
        ticketId: id,
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
    });
  };


  const renderTicketInfo = () => {
    if (ticketUser) {

      return (
        <>
          {ticket.chatbot && (
            <Tooltip title="Chatbot">
              <AndroidIcon
                fontSize="small"
                style={{ color: grey[700], marginRight: 5 }}
              />
            </Tooltip>
          )}

          {/* </span> */}
        </>
      );
    } else {
      return (
        <>
          {ticket.chatbot && (
            <Tooltip title="Chatbot">
              <AndroidIcon
                fontSize="small"
                style={{ color: grey[700], marginRight: 5 }}
              />
            </Tooltip>
          )}
        </>
      );
    }
  };

  const renderMessageCheck = () => {
    // ack = 0 ou 1: Pendente (não exibe)
    // ack = 2: Enviada (check único cinza)
    // ack = 3: Recebida/Entregue (check duplo cinza)
    // ack = 4: Lida (check duplo azul)
    const rawAck = ticket?.effectiveLastMessageAck ?? ticket?.lastMessageAck ?? 0;
    const ack = Number.isFinite(Number(rawAck)) ? Number(rawAck) : 0;

    // ack = -1: falha de envio. Precisa vir antes de tudo — sem este guard o -1
    // escorrega até o return final e a lista mostra check duplo de "entregue"
    // justamente para a mensagem que não saiu.
    if (ack === -1) {
      return (
        <ErrorOutlineIcon className={classes.messageCheckIconError} />
      );
    }

    if (ack === 0 || ack === 1) {
      return null; // Não exibe nada se pendente
    }
    
    if (ack === 2) {
      // Check único cinza - mensagem enviada
      return (
        <DoneIcon className={classes.messageCheckIcon} />
      );
    }
    
    if (ack === 4 || ack === 5) {
      // Check duplo azul - mensagem lida
      return (
        <DoneAllIcon className={classes.messageCheckIconRead} />
      );
    }
    
    // Check duplo cinza - mensagem recebida (ack = 3)
    return (
      <DoneAllIcon className={classes.messageCheckIcon} />
    );
  };

  const renderConnectionIcon = () => (
    <ChannelIcon type={ticket?.whatsapp?.type} style={{ fontSize: 13 }} />
  );

  const connectionTooltip = `${i18n.t("contactChat.connection", { name: ticket?.whatsapp?.name || "" })}${ticket?.whatsapp?.type === "instagram" && ticket.channel === "comment" ? ` • ${i18n.t("ticketsList.item.comment")}` : ""}`;

  // Quando o card exibe o badge vermelho (contador de não lidas ou a bolinha de
  // "marcado como não lido"), ele avança sobre a direita e cobriria o atendente
  const hasUnreadBadge = ticket.unreadMessages > 0 || Boolean(ticket.markedAsUnread);

  const queueName = ticket.queue?.name || i18n.t("contactChat.otherUsers.noQueue");
  // Ticket de conexão excluída: `ticket.whatsapp` vem nulo e o lugar do badge da
  // conexão passa a ser o selo "Conexão excluída".
  const isInactive = isInactiveTicket(ticket);
  const connectionName = isInactive ? i18n.t("tickets.inactiveConnection.badge") : ticket?.whatsapp?.name;
  const hasConnectionBadge = isInactive || Boolean(ticket?.whatsapp?.id);

  // Cada item declara quanto gasta fora do texto, seu piso em caracteres e quem
  // cede espaço primeiro; useBadgeRowWidths mede a linha e devolve as larguras.
  const badgeRowItems = useMemo(() => {
    if (!badgesLayout) return [];

    const items = [
      {
        key: "queue",
        text: queueName,
        chrome: INFO_BADGE_CHROME,
        minChars: BADGE_MIN_CHARS,
        letterSpacing: INFO_BADGE_LETTER_SPACING,
        priority: SHRINK_FIRST,
      },
    ];

    if (hasConnectionBadge) {
      items.push({
        key: "connection",
        text: connectionName,
        chrome: INFO_BADGE_CHROME,
        minChars: BADGE_MIN_CHARS,
        letterSpacing: INFO_BADGE_LETTER_SPACING,
        priority: SHRINK_FIRST,
      });
    }

    if (ticketUser) {
      items.push({
        key: "attendant",
        text: ticketUser,
        chrome: ATTENDANT_CHROME + (hasUnreadBadge ? ATTENDANT_UNREAD_OFFSET : 0),
        minChars: ATTENDANT_MIN_CHARS,
        maxWidth: BADGE_MAX_WIDTH,
        priority: SHRINK_LAST,
      });
    }

    return items;
  }, [
    badgesLayout,
    queueName,
    connectionName,
    hasConnectionBadge,
    ticketUser,
    hasUnreadBadge,
  ]);

  const { ref: badgeRowRef, widths: badgeWidths } = useBadgeRowWidths(
    badgeRowItems,
    { gap: BADGE_ROW_GAP, enabled: badgesLayout }
  );

  // Largura calculada vira width fixo: o flexbox não pode mais encolher nem
  // esticar o item por conta própria (senão desfaz a prioridade acima).
  const badgeItemStyle = (key) =>
    badgeWidths[key] === undefined
      ? null
      : {
          width: badgeWidths[key],
          minWidth: 0,
          maxWidth: "none",
          flexShrink: 0,
          flexGrow: 0,
        };

  const renderAttendantChip = () => {
    if (!ticketUser) return null;
    return (
      <Tooltip arrow placement="top" title={i18n.t("ticketsList.item.attendantLabel", { name: ticketUser })}>
        <span
          className={clsx(classes.attendantChip, {
            [classes.attendantChipUnread]: hasUnreadBadge,
          })}
        >
          <HeadsetMicIcon className={classes.attendantIcon} />
          <span className={classes.attendantName}>{ticketUser}</span>
        </span>
      </Tooltip>
    );
  };

  // Layout 2 (REACT_APP_TICKET_CARD_LAYOUT=badges): fila e conexão viram badges
  // coloridos com ícone, lado a lado, quebrando linha só quando não couberem.
  const renderBadgesBottom = () => {
    const queueColor = ticket.queue?.color || "#7c7c7c";
    const connectionColor = ticket?.whatsapp?.color || "#7c7c7c";
    // contrastColor só sabe ler hex #rrggbb; para qualquer outro formato usa branco
    const textOn = (bg) => (/^#[0-9a-f]{6}$/i.test(bg) ? contrastColor(bg) : "#fff");

    return (
      <span className={classes.badgeRows} ref={badgeRowRef}>
        <Tooltip arrow placement="top" title={i18n.t("ticketsList.item.queueLabel", { name: queueName })}>
          <span
            className={classes.infoBadge}
            style={{
              backgroundColor: queueColor,
              color: textOn(queueColor),
              ...badgeItemStyle("queue"),
            }}
          >
            <AccountTreeOutlinedIcon />
            <span className={classes.infoBadgeLabel} {...{ [BADGE_TEXT_ATTR]: "" }}>
              {queueName}
            </span>
          </span>
        </Tooltip>
        {isInactive && (
          <InactiveConnectionBadge
            ticket={ticket}
            className={classes.infoBadge}
            style={badgeItemStyle("connection")}
          />
        )}
        {hasConnectionBadge && !isInactive && (
          <Tooltip arrow placement="top" title={connectionTooltip}>
            <span
              className={classes.infoBadge}
              style={{
                backgroundColor: connectionColor,
                color: textOn(connectionColor),
                ...badgeItemStyle("connection"),
              }}
            >
              {renderConnectionIcon()}
              <span className={classes.infoBadgeLabel}>{connectionName}</span>
            </span>
          </Tooltip>
        )}
        {ticketUser && (
          <span
            className={classes.badgeAttendant}
            style={badgeItemStyle("attendant")}
          >
            {renderAttendantChip()}
          </span>
        )}
      </span>
    );
  };

  // Handler para clique no checkbox
  const handleCheckboxClick = (e) => {
    e.stopPropagation();
    if (canSelect && onSelect) {
      onSelect(ticket);
    }
  };

  // Handler para menu de contexto (clique direito)
  const handleRightClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onContextMenu) {
      onContextMenu(e, ticket);
    }
  };

  // Handler para CTRL+click (seleção)
  const handleCtrlClick = (e) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      e.stopPropagation();
      
      // Ativar modo seleção se não estiver ativo
      if (!selectionMode && onSelect) {
        // Primeiro, precisamos ativar o modo seleção via context menu handler
        // Simular o comportamento de selecionar
        if (onContextMenu) {
          // Criar evento sintético para ativar seleção
          const syntheticEvent = {
            ...e,
            preventDefault: () => {},
            stopPropagation: () => {},
            ctrlClick: true,
          };
          // Passar pro pai que vai ativar o modo seleção
        }
      }
      
      if (canSelect && onSelect) {
        onSelect(ticket);
      }
      return true;
    }
    return false;
  };

  // Handler para clique no item
  const handleItemClick = (e) => {
    // Verificar CTRL+click primeiro
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      e.stopPropagation();
      if (canSelect && onSelect) {
        onSelect(ticket);
      }
      return;
    }
    
    if (selectionMode && canSelect) {
      // Em modo seleção, clique seleciona/deseleciona
      handleCheckboxClick(e);
    } else {
      // Modo normal, abre o ticket
      handleSelectTicket(ticket);
    }
  };

  return (
    <React.Fragment key={ticket.id}>
      {closeGuardDialog}
      <TicketMessagesDialog
        open={openTicketMessageDialog}

        handleClose={() => setOpenTicketMessageDialog(false)}
        ticketId={ticket.id}
      ></TicketMessagesDialog>
      <ListItem dense button
        onClick={handleItemClick}
        onContextMenu={handleRightClick}
        selected={ticketId && +ticketId === ticket.id}
        className={clsx(classes.ticket, {
          [classes.ticketBadgesLayout]: badgesLayout,
          [classes.pendingTicket]: ticket.status === "pending",
          [classes.selectedTicket]: isSelected,
          [classes.ticketWithCheckbox]: selectionMode,
          [classes.ticketAlarm]: Boolean(ticket.isInAlarm),
        })}
      >
        {/* Checkbox para seleção múltipla */}
        {selectionMode && (
          <div className={classes.checkboxContainer}>
            <Checkbox
              checked={isSelected}
              onChange={handleCheckboxClick}
              onClick={(e) => e.stopPropagation()}
              color="primary"
              size="small"
              disabled={!canSelect}
            />
          </div>
        )}
        
        <Tooltip arrow placement="right" title={ticket.queue?.name?.toUpperCase() || i18n.t("contactChat.otherUsers.noQueue").toUpperCase()} >
          <span style={{ backgroundColor: ticket.queue?.color || "#7C7C7C" }} className={classes.ticketQueueColor}></span>
        </Tooltip>
        <ListItemAvatar style={{ display: "flex", alignItems: "center", justifyContent: "center", minWidth: 60 }}>
          {ticket?.whatsapp?.type === "email" ? (
            <Avatar
              style={{
                width: "50px",
                height: "50px",
                borderRadius: "10%",
                backgroundColor: "#1976d2",
                color: "#fff",
              }}
            >
              <EmailIcon />
            </Avatar>
          ) : (
            <ContactAvatar
              contact={ticket?.contact}
              style={{
                width: "50px",
                height: "50px",
                borderRadius: "10%",
              }}
            />
          )}
        </ListItemAvatar>
        <ListItemText
          disableTypography

          primary={
            <span className={classes.contactNameWrapper}>
              <Typography
                noWrap
                component="span"
                variant="body2"
                color="textPrimary"
                className={clsx({ [classes.contactNameBadges]: badgesLayout })}
              >
                {(() => {
                  const displayName = ticket?.whatsapp?.type === "email"
                    ? (ticket?.contact?.email || ticket?.contact?.name)
                    : ticket?.contact?.name;
                  return displayName?.length > 22
                    ? `${displayName?.substring(0, 22)}...`
                    : displayName;
                })()}
              </Typography>
              <Box className={classes.ticketInfo1}>{renderTicketInfo()}</Box>
            </span>

          }
          secondary={
            <span className={classes.secondaryWrapper}>
              <Typography
                className={classes.contactLastMessage}
                noWrap
                component="span"
                variant="body2"
                color="textSecondary"
                translate="no"
              >
                {contactTyping ? (
                  <span className={classes.contactTyping}>
                    {i18n.t(
                      contactTyping === "recording"
                        ? "contactWhatsappStatus.recording"
                        : "contactWhatsappStatus.typing"
                    )}
                  </span>
                ) : (
                  <>
                    {renderMessageCheck()}
                    {getLastMessagePreview(ticket?.lastMessage)}
                  </>
                )}
              </Typography>
              {badgesLayout ? renderBadgesBottom() : (
                <span className={classes.bottomRow}>
                  <span
                    className={classes.queueLine}
                    style={{ color: ticket.queue?.color || "#7c7c7c" }}
                  >
                    {i18n.t("ticketsList.item.queueLabel", { name: ticket.queue?.name || i18n.t("contactChat.otherUsers.noQueue") })}
                  </span>
                  <span className={classes.bottomRightInfo}>
                    {renderAttendantChip()}
                    {isInactive && <InactiveConnectionBadge ticket={ticket} compact />}
                    {ticket?.whatsapp?.id && (
                      <Tooltip arrow placement="top" title={connectionTooltip}>
                        <span
                          className={classes.connectionChip}
                          style={{ backgroundColor: ticket?.whatsapp?.color || "#7c7c7c" }}
                        >
                          {renderConnectionIcon()}
                        </span>
                      </Tooltip>
                    )}
                  </span>
                </span>
              )}
              {ticket?.promptId && (
                <span className={classes.secondaryContentSecond} >
                  <Tooltip arrow placement="top" title={i18n.t("ticketsList.item.aiAgentLabel", { name: aiAgentName || "" })}>
                    <Badge style={{ backgroundColor: "#6a1b9a" }} className={classes.connectionTag}>
                      <RobotIcon style={{ fontSize: "1.2em", verticalAlign: "-0.25em", marginRight: 3 }} />
                      {(aiAgentName || "...").toUpperCase()}
                    </Badge>
                  </Tooltip>
                </span>
              )}
              <span style={{ paddingTop: "2px" }} className={classes.secondaryContentSecond} >
                {tag?.map((tag) => {
                  return (
                    <ContactTag tag={tag} key={`ticket-contact-tag-${ticket.id}-${tag.id}`} />
                  );
                })}
              </span>
            </span>
          }

        />
        <span className={classes.secondaryContentSecond} style={{ display: "none" }}>
          {ticket.status === "pending" && (
            <ButtonWithSpinner
              //color="primary"
              style={{ backgroundColor: 'green', color: 'white', padding: '0px', bottom: '17px', borderRadius: '0px', left: '8px', fontSize: '0.6rem' }}
              variant="contained"
              className={classes.acceptButton}
              size="small"
              loading={loading}
			  //PLW DESIGN INSERIDO O handleChangeTab
              onClick={e => {
                e.stopPropagation();
                handleAcepptTicket(ticket.id);
              }}
            >
              {i18n.t("ticketsList.buttons.accept")}
            </ButtonWithSpinner>

          )}
          {(ticket.status !== "closed") && (
            <ButtonWithSpinner
              //color="primary"
              style={{ backgroundColor: 'red', color: 'white', padding: '0px', bottom: '0px', borderRadius: '0px', left: '8px', fontSize: '0.6rem' }}
              variant="contained"
              className={classes.acceptButton}
              size="small"
              loading={loading}
              onClick={e => {
                e.stopPropagation();
                handleCloseTicket(ticket.id);
              }}
            >
              {i18n.t("ticketsList.buttons.closed")}
            </ButtonWithSpinner>

          )}
          {(ticket.status === "closed" && !isInactive) && (
            <ButtonWithSpinner
              //color="primary"
              style={{ backgroundColor: 'red', color: 'white', padding: '0px', bottom: '0px', borderRadius: '0px', left: '8px', fontSize: '0.6rem' }}
              variant="contained"
              className={classes.acceptButton}
              size="small"
              loading={loading}
              onClick={e => {
                e.stopPropagation();
                handleReopenTicket(ticket.id);
              }}
            >
              {i18n.t("ticketsList.buttons.reopen")}
            </ButtonWithSpinner>

          )}
        </span>
        <ListItemSecondaryAction style={{ top: 16, right: 16, transform: 'none', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          <Typography
            className={clsx(classes.lastMessageTime, {
              [classes.lastMessageTimeAlarm]: Boolean(ticket.isInAlarm),
            })}
            component="span"
            variant="body2"
            color="textSecondary"
          >
            {isSameDay(parseISO(ticket.updatedAt), new Date()) ? (
              <>{format(parseISO(ticket.updatedAt), "HH:mm")}</>
            ) : (
              <>{format(parseISO(ticket.updatedAt), "dd/MM/yyyy")}</>
            )}
          </Typography>
          {/* Sino cortado + contador na mesma linha, como no WhatsApp. O Badge
              não tem filho: o pino dele fica 20px ACIMA da própria origem, e a
              origem é empurrada 20px por newMessagesCount. Com a linha em
              align-items:center essas duas alturas se cancelam e o sino alinha
              com o pino sem precisar de ajuste fino. */}
          <span className={classes.mutedBadgeRow}>
            {isTicketMuted && (
              <Tooltip arrow placement="top" title={i18n.t("tickets.muted")}>
                <MutedIcon
                  fontSize="small"
                  className={clsx(classes.mutedIcon, {
                    // O pino transborda meia largura para a direita da origem;
                    // sem essa folga ele cobriria o sino.
                    [classes.mutedIconWithBadge]: hasUnreadBadge,
                  })}
                />
              </Tooltip>
            )}
            {ticket.unreadMessages > 0 && (
              <Badge
                className={classes.newMessagesCount}
                badgeContent={ticket.unreadMessages}
                classes={{
                  badge: classes.badgeStyle,
                }}
              />
            )}
            {ticket.markedAsUnread && ticket.unreadMessages === 0 && (
              <Badge
                className={clsx(classes.newMessagesCount, classes.unreadDotBadge)}
                variant="dot"
                classes={{
                  badge: classes.badgeStyle,
                }}
              />
            )}
          </span>
        </ListItemSecondaryAction>
      </ListItem>

      <Divider variant="inset" component="li" />
    </React.Fragment>
  );
};

export default TicketListItemCustom;